import { useState } from 'react';
import { FileClock, Search, X } from 'lucide-react';
import {
  useGetApiV1Audit,
  useGetApiV1AuditEntityTypes,
} from '@/shared/api/generated/audit/audit';
import type { AuditEntryDto, GetApiV1AuditParams } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { QueryError } from '@/shared/ui/QueryError';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Pagination } from '@/shared/ui/Pagination';
import { TableSkeleton } from '@/shared/ui/TableSkeleton';
import { EmptyState } from '@/shared/ui/EmptyState';

/**
 * ⚠️ **The kinds are asked for, not typed out — because a hand-written list went wrong twice.**
 *
 * §12.7 logs every state change, so the trail is dominated by the machine: measured on 2 September
 * 2026, **87.3%** of it was `TrackPoint`, one row per position a rescuer reports. The filter is
 * therefore not a refinement, it is what makes the screen usable at all — and it was a curated list
 * of twelve names against a trail that holds twenty-four.
 *
 * It was wrong in both directions. `Vote` is a name nothing ever writes — the entity is
 * `CommitteeVote` — so choosing it returned an empty table, which on an audit screen reads as
 * "nobody did anything". And `Role`, `PointRule`, `Certificate` and every report type were absent,
 * so "who changed the points table", "who altered a role" and "who approved the report" could not
 * be asked at all.
 *
 * The curation had a reason — an administrator should not have to guess the spelling of a class
 * name — and a dropdown of twenty-four sorted names still does not make them. What curation cost
 * was correctness, twice, and the server can answer this without anybody maintaining it.
 */

const ACTIONS = ['Added', 'Modified', 'Deleted', 'Read'] as const;

const PAGE_SIZE = 25;

/**
 * The audit trail (§12.7 — a named contract deliverable).
 *
 * ⚠️ **The log has been written since the first commit and nothing could read it.** An audit trail
 * whose only reader is a database client is a compliance record the association cannot use.
 */
export function AuditTrail() {
  const { t, locale } = useLocale();

  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState<string>('');

  // ⚠️ A failure here leaves the dropdown with «كلّ الأنواع» alone rather than blocking the screen.
  // The trail is what the administrator came for; losing the filter is a smaller loss than losing
  // the rows, and the request is one small array that the browser caches.
  const entityTypes = useGetApiV1AuditEntityTypes();
  const [action, setAction] = useState<string>('');
  const [actorId, setActorId] = useState('');

  // ⚠️ Off by default — the association's decision of 5 September 2026. Every token rotation
  // writes two rows and the first page was twenty of them to five human acts. The rows are still
  // written (a stolen token shows up there); this only decides what the page opens on.
  const [includeSystem, setIncludeSystem] = useState(false);

  const params: GetApiV1AuditParams = {
    Page: page,
    PageSize: PAGE_SIZE,
    ...(entityType ? { EntityType: entityType } : {}),
    ...(action ? { Action: action } : {}),
    ...(actorId.trim() ? { ActorId: actorId.trim() } : {}),
    ...(includeSystem ? { IncludeSystem: true } : {}),
  };

  const { data, isPending, isError, error, refetch } = useGetApiV1Audit(params);

  const narrowed = Boolean(entityType || action || actorId.trim());

  const clear = () => {
    setEntityType('');
    setAction('');
    setActorId('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t.audit.title} lede={t.audit.subtitle} />

      <Panel title={t.audit.filters}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
          <Field label={t.audit.entityType} htmlFor="audit-entity">
            <Select
              value={entityType || 'all'}
              onValueChange={(value) => {
                setEntityType(value === 'all' ? '' : value);
                setPage(1);
              }}
            >
              <SelectTrigger id="audit-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.audit.anyEntity}</SelectItem>
                {(entityTypes.data ?? []).map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          </div>

          <div className="min-w-[160px]">
          <Field label={t.audit.action} htmlFor="audit-action">
            <Select
              value={action || 'all'}
              onValueChange={(value) => {
                setAction(value === 'all' ? '' : value);
                setPage(1);
              }}
            >
              <SelectTrigger id="audit-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.audit.anyAction}</SelectItem>
                {ACTIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t.audit.actions[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          </div>

          <div className="min-w-[280px] flex-1">
          <Field label={t.audit.actor} htmlFor="audit-actor">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 start-3" />
              <input
                id="audit-actor"
                className="h-11 w-full rounded-lg border border-gray-200 bg-white ps-9 pe-3 text-sm outline-none focus:border-brand-300"
                placeholder={t.audit.actorPlaceholder}
                value={actorId}
                onChange={(event) => {
                  setActorId(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </Field>
          </div>

          <label className="flex items-center gap-2 self-end pb-3 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeSystem}
              onChange={(event) => {
                setIncludeSystem(event.target.checked);
                setPage(1);
              }}
            />
            {t.audit.includeSystem}
          </label>

          {narrowed && (
            <Button variant="ghost" onClick={clear}>
              <X className="h-4 w-4" />
              {t.audit.clear}
            </Button>
          )}
        </div>
      </Panel>

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      <Panel title={t.audit.entries} bodyClassName="flex flex-col">
        {/*
          ⚠️ **A query that FAILED must not draw the empty state.** «لم يُسجَّل شيء بعد» under an
          audit trail is a claim that nothing happened, and here it was drawn from a request that
          never returned — measured on 2 September 2026, where a head of control opened this route,
          the API answered 403, and the page said the refusal AND «لا سجلّات» in the same breath.
          Of all the screens to say that about, this is the worst one.

          The pattern already lives in `RoleManager`: error before empty, because an operator
          without the Admin policy can reach these routes.
        */}
        {isError ? null : isPending ? (
          <TableSkeleton rows={6} columns={5} />
        ) : data && data.items && data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.audit.when}</TableHead>
                  <TableHead>{t.audit.actor}</TableHead>
                  <TableHead>{t.audit.action}</TableHead>
                  <TableHead>{t.audit.entityType}</TableHead>
                  <TableHead>{t.audit.change}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((entry) => (
                  <Row key={entry.id} entry={entry} locale={locale} />
                ))}
              </TableBody>
            </Table>

            <Pagination
              page={data.page ?? page}
              pageSize={data.pageSize ?? PAGE_SIZE}
              totalCount={data.totalCount ?? 0}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={FileClock}
            title={t.audit.emptyTitle}
            hint={narrowed ? t.audit.emptyNarrowed : t.audit.empty}
          />
        )}
      </Panel>
    </div>
  );
}

function Row({ entry, locale }: { entry: AuditEntryDto; locale: string }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell className="whitespace-nowrap text-sm text-gray-500">
          {/* ⚠️ Gregorian and explicit: an unqualified date on a machine set to Umm al-Qura renders
              Hijri, and an audit trail whose timestamps disagree with the server's log is unusable
              as evidence — which is the one thing it exists to be. */}
          {new Date(entry.occurredAt).toLocaleString(`${locale}-u-ca-gregory`)}
        </TableCell>

        <TableCell className="text-sm">
          {/* ⚠️ Not "unknown". The system itself acts — a sweep, a job, an escalation, and every row
              written during sign-in before the caller is known — and "unknown user" reads as a fault
              an auditor then goes looking for. */}
          {entry.actorName ?? <span className="text-gray-400">{t.audit.bySystem}</span>}
        </TableCell>

        <TableCell className="text-sm">
          {t.audit.actions[entry.action as keyof typeof t.audit.actions] ?? entry.action}
        </TableCell>

        <TableCell className="text-sm">{entry.entityType}</TableCell>

        <TableCell>
          {entry.before || entry.after ? (
            <Button variant="ghost" onClick={() => setOpen((value) => !value)}>
              {open ? t.audit.hide : t.audit.show}
            </Button>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </TableCell>
      </TableRow>

      {open && (
        <TableRow>
          <TableCell colSpan={5} className="bg-gray-50">
            <div className="grid gap-4 md:grid-cols-2">
              <Snapshot label={t.audit.before} value={entry.before} />
              <Snapshot label={t.audit.after} value={entry.after} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * ⚠️ Forced left to right and monospaced. This is JSON: an Arabic page mirrors its brackets and
 * turns a snapshot somebody is comparing against a server log into a different document.
 */
function Snapshot({ label, value }: { label: string; value?: string | null }) {
  const { t } = useLocale();

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-gray-500">{label}</p>
      {value ? (
        <pre
          dir="ltr"
          className="max-h-64 overflow-auto rounded-lg bg-white p-3 text-start font-mono text-xs text-gray-700"
        >
          {pretty(value)}
        </pre>
      ) : (
        <p className="text-xs text-gray-400">{t.audit.nothing}</p>
      )}
    </div>
  );
}

/**
 * ⚠️ Falls back to the raw string rather than throwing. The column is written by an interceptor and
 * read here; a row this build cannot parse is still evidence, and an audit screen that renders a
 * blank where a record exists is worse than one that shows an awkward line.
 */
function pretty(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}
